import { generatePath } from 'react-router-dom';
import { zip } from 'rxjs/observable/zip';
import { of } from 'rxjs/observable/of';
import { merge } from 'rxjs/observable/merge';

import { mergeMap } from 'rxjs/operators/mergeMap';
import { delay } from 'rxjs/operators/delay';
import { empty } from 'rxjs/observable/empty';
import { map } from 'rxjs/operators/map';
import { withLatestFrom } from 'rxjs/operators/withLatestFrom';
import { filter } from 'rxjs/operators/filter';
import { catchError } from 'rxjs/operators/catchError';
import { tap } from 'rxjs/operators/tap';

import { combineEpics, ofType } from 'redux-observable';
import queryString from 'querystring';

import {
  abortTransactionMutation,
  executeCartTypeCheckQuery,
  placeWithMethodMutation,
} from './helpers/transaction';
import messages from './transaction.messages';

import ReturnFromPayPalMutation from '../graphql/ReturnFromPayPalMutation';
import ReturnFromWorldpayMutation from '../graphql/ReturnFromWorldpayMutation';
import ReturnFromWorldpayPendingMutation from '../graphql/ReturnFromWorldpayPendingMutation';
import { setUrl, setRoute, SYNC_ROUTE } from '../actions/routes';
import {
  START_CANCEL_TRANSACTION,
  endCancelTransaction,
  receivePaymentResponse,
  PLACE_ORDER,
  startWorldPayModal,
} from '../actions/transaction';
import { getPlaceOrderFallbackRoute } from '../helpers/checkout';
import { closeModal, openModal } from '../redux/modal';
import { LEGACY_CART } from '../constants/urls';
import CHECKOUT_STEPS_ROUTES from '../routes/checkout';

import { withPaymentResponse, withPendingStatus } from './helpers/operators';

import CancelTransactionMessage, {
  label as cancelTransactionLabel,
} from '../components/Modal/messages/CancelTransactionMessage';

import { CartTypeQuery } from '../graphql/cart';
import logger from './helpers/logger';
import { catchUnexpectedErrors } from './helpers/errors';
import { pushError } from '../actions/errors';

const cancelTransactionEpic = (action$, store, { apolloClient }) =>
  action$.ofType(START_CANCEL_TRANSACTION).pipe(
    mergeMap(() =>
      zip(
        abortTransactionMutation(apolloClient),
        executeCartTypeCheckQuery(apolloClient)
      )
    ),
    mergeMap((results) => {
      const {
        data: {
          user: {
            cart: { type },
          },
        },
      } = results[1];

      // We need to check the cart again for the case if the user completed
      // the checkout inside of another tab
      if (type === 'EMPTY') {
        return of(setUrl(LEGACY_CART));
      }

      return of(endCancelTransaction(), closeModal());
    })
  );

const paymentResponseEpic = (action$, store, { apolloClient }) =>
  action$.ofType(SYNC_ROUTE).pipe(
    filter((route) => route.match.path === CHECKOUT_STEPS_ROUTES.response.path),
    mergeMap((route) => {
      const search = route.location.search.substring(1);
      const { action, method, ...data } = queryString.parse(search);

      if (action) {
        return of(receivePaymentResponse(method, action, data));
      }

      if (
        method === 'paypal_express' ||
        method === 'paypal_installments' ||
        method === 'paypal_credit'
      ) {
        // PayPal does not send the status information in the return url
        // Therefore we need to make a call to the server to trigger additional
        // logic and determine whether the payment process was successful
        return apolloClient
          .mutate({
            mutation: ReturnFromPayPalMutation,
            variables: {
              token: data.token,
              payerId: data.PayerID,
              cartId: data.cartId,
            },
          })
          .then(
            (response) =>
              receivePaymentResponse(method, 'success', {
                ...data,
                orderNumber:
                  response.data.user.cart.returnFromPayPal.reservedOrderNumber,
              }),
            () => receivePaymentResponse(method, 'failure', data)
          );
      }

      return of(receivePaymentResponse(method, 'failure', data));
    })
  );

const cancelResponseEpic = (action$, store, { apolloClient }) =>
  action$.pipe(
    withPaymentResponse('cancel'),
    mergeMap(() => abortTransactionMutation(apolloClient)),
    mergeMap(() => of(closeModal(), setRoute(getPlaceOrderFallbackRoute())))
  );

const successResponseEpic = (action$, store, { apolloClient }) =>
  action$.pipe(
    withPaymentResponse('success'),
    mergeMap(({ method, data }) => {
      if (method === 'worldpay') {
        return apolloClient
          .mutate({
            mutation: ReturnFromWorldpayMutation,
            variables: {
              orderKey: data.orderKey,
              paymentAmount: data.paymentAmount,
              paymentCurrency: data.paymentCurrency,
              paymentStatus: data.paymentStatus,
              mac: data.mac,
              cartId: data.cartId,
            },
          })
          .then(
            (response) =>
              response.data.user.cart.returnFromWorldpay.reservedOrderNumber
          );
      }
      return of(data.orderNumber);
    }),
    mergeMap((orderNumber) =>
      of(
        closeModal(),
        setRoute(CHECKOUT_STEPS_ROUTES.success.path, {
          orderNumber,
        })
      )
    )
  );

const errorResponseEpic = (action$, store, { apolloClient }) =>
  merge(
    action$.pipe(withPaymentResponse('failure')),
    action$.pipe(withPendingStatus('ERROR', 'FAILURE', 'EXPIRED'))
  ).pipe(
    tap(() => abortTransactionMutation(apolloClient)),
    mergeMap((res) =>
      merge(
        of(
          closeModal(),
          setRoute(getPlaceOrderFallbackRoute())
          // TODO: Use proper & translated error message
        ),
        of(
          pushError(
            res.data.status === 'EXPIRED'
              ? messages.pendingExpired
              : messages.paymentError
          )
        ).pipe(delay(10))
      )
    )
  );

const pendingResponseEpic = (action$, store, { apolloClient }) =>
  action$.pipe(
    withPendingStatus('OPEN'),
    mergeMap(({ method, data }) => {
      if (method === 'worldpay') {
        return apolloClient
          .mutate({
            mutation: ReturnFromWorldpayPendingMutation,
            variables: {
              orderKey: data.orderKey,
              paymentStatus: data.status,
              cartId: data.cartId,
            },
          })
          .then(
            (response) =>
              response.data.user.cart.returnFromWorldpay.reservedOrderNumber
          );
      }
      return of(data.orderNumber);
    }),
    mergeMap((orderNumber) =>
      of(
        closeModal(),
        setRoute(CHECKOUT_STEPS_ROUTES.pending.path, {
          orderNumber,
        })
      )
    )
  );

const getResponseUrl = (baseUrl, cartId, orderNumber, paymentMethod) => {
  if (paymentMethod.action === 'iframe') {
    return '';
  }

  return `${baseUrl}?method=${paymentMethod.method}&orderNumber=${orderNumber}&cartId=${cartId}`;
};

const placeOrderEpic = (action$, store, { apolloClient, baseUrl }) =>
  action$.pipe(
    ofType(PLACE_ORDER),
    logger('placeOrderEpic.PLACE_ORDER'),
    withLatestFrom(action$.pipe(ofType(SYNC_ROUTE))),
    mergeMap(([{ paymentMethod }, { match: { params: { language } } }]) =>
      of(paymentMethod).pipe(
        logger('placeOrderEpic.paymentMethod'),
        mergeMap(() =>
          apolloClient.query({
            query: CartTypeQuery,
          })
        ),
        logger('placeOrderEpic.CartTypeQuery'),
        mergeMap(({ data }) =>
          placeWithMethodMutation(apolloClient, {
            method: paymentMethod.method,
            cardType: paymentMethod.cardType,
            cartId: data.user.cart.id,
            responseUrl: getResponseUrl(
              `${baseUrl}${generatePath(CHECKOUT_STEPS_ROUTES.response.path, {
                language,
              })}`,
              data.user.cart.id,
              data.user.cart.reservedOrderNumber,
              paymentMethod
            ),
          })
        ),
        logger('placeOrderEpic.placeWithMethodMutation'),
        map((response) => {
          const {
            data: {
              user: {
                cart: { placeWithMethod: cart },
              },
            },
          } = response;

          switch (cart.paymentMethod.action) {
            case 'iframe':
              return startWorldPayModal(cart);
            case 'success':
              return receivePaymentResponse(
                cart.paymentMethod.method,
                'success',
                {
                  orderNumber: cart.reservedOrderNumber,
                }
              );
            case 'redirect':
              return setUrl(cart.paymentMethod.url);
            default:
              // Unexpected case is treated as an error
              return receivePaymentResponse(
                cart.paymentMethod.method,
                'failure',
                {}
              );
          }
        }),
        logger('placeOrderEpic.response'),
        catchError((error) => {
          if (!error.graphQLErrors) {
            throw error;
          }
          const hasTransactionStartedError = error.graphQLErrors.some(
            (err) =>
              err.id === 'illegal_transition_transaction_started_modify_quote'
          );

          if (hasTransactionStartedError) {
            return of(
              openModal({
                content: <CancelTransactionMessage />,
                label: cancelTransactionLabel,
              })
            );
          }
          // Ignore the error details here.
          // They will be pushed to the notification area
          // by the global error epic
          return empty();
        }),
        catchUnexpectedErrors(),
        logger('placeOrderEpic.catchError')
      )
    )
  );

const transactionEpic = combineEpics(
  placeOrderEpic,
  paymentResponseEpic,
  successResponseEpic,
  cancelTransactionEpic,
  cancelResponseEpic,
  errorResponseEpic,
  pendingResponseEpic
);

export {
  placeOrderEpic,
  paymentResponseEpic,
  successResponseEpic,
  cancelTransactionEpic,
  cancelResponseEpic,
  errorResponseEpic,
  pendingResponseEpic,
};

export default transactionEpic;
